import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Save, Play, Dice5, CircleDot, Grid3x3, Link as LinkIcon, Lock, X, AlertTriangle, Target, Trophy } from "lucide-react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";

// ─── Helpers ──────────────────────────────────────────────
function wingoColor(n) {
  if (n === 0) return ["red", "violet"];
  if (n === 5) return ["green", "violet"];
  return [n % 2 === 1 ? "green" : "red"];
}
const COLOR_CLASSES = {
  red:    { bg: "bg-rose-500",   ring: "ring-rose-400",   text: "text-rose-500",   soft: "bg-rose-50" },
  green:  { bg: "bg-emerald-500", ring: "ring-emerald-400", text: "text-emerald-500", soft: "bg-emerald-50" },
  violet: { bg: "bg-violet-500", ring: "ring-violet-400", text: "text-violet-500", soft: "bg-violet-50" },
};
function WingoNumberBadge({ n, size = "md" }) {
  const colors = wingoColor(n);
  const dim = size === "lg" ? "w-12 h-12 text-xl" : size === "sm" ? "w-6 h-6 text-xs" : "w-8 h-8 text-sm";
  if (colors.length === 1) {
    return <span className={`inline-flex items-center justify-center rounded-full font-bold text-white ${dim} ${COLOR_CLASSES[colors[0]].bg}`}>{n}</span>;
  }
  // dual-color split circle
  return (
    <span className={`inline-flex items-center justify-center rounded-full font-bold text-white ${dim} relative overflow-hidden`}
      style={{ background: `linear-gradient(135deg, var(--c1) 0%, var(--c1) 50%, var(--c2) 50%, var(--c2) 100%)`,
               "--c1": colors[0] === "red" ? "#f43f5e" : "#10b981",
               "--c2": "#8b5cf6" }}>
      <span className="relative z-10">{n}</span>
    </span>
  );
}
function fmtResultPretty(game, r) {
  if (!r) return null;
  if (game === "wingo" || game === "trxwin") {
    return <span className="inline-flex items-center gap-2"><WingoNumberBadge n={r.number} size="sm" /><span className="capitalize">{(r.color || []).join(" · ")}</span><span className="capitalize">· {r.size}</span></span>;
  }
  if (game === "k3") {
    return <span className="font-mono">🎲 {(r.dice || []).join(" · ")} (Σ{r.sum}, {r.size}, {r.parity})</span>;
  }
  if (game === "5d") {
    return <span className="font-mono">{(r.digits || []).join(" ")} (Σ{r.sum})</span>;
  }
  return <span className="font-mono text-xs">{JSON.stringify(r)}</span>;
}

const GAMES = [
  { key: "wingo", label: "Win Go", icon: CircleDot },
  { key: "trxwin", label: "TRX Win", icon: LinkIcon },
  { key: "k3", label: "K3 Lotre", icon: Dice5 },
  { key: "5d", label: "5D Lotre", icon: Grid3x3 },
];

const DURATIONS = [
  { s: 30, label: "30s" },
  { s: 60, label: "1min" },
  { s: 180, label: "3min" },
  { s: 300, label: "5min" },
  { s: 600, label: "10min" },
];

function fmtMoney(v) {
  return `৳ ${Number(v || 0).toFixed(2)}`;
}

export default function LotteryAdmin() {
  const [tab, setTab] = useState("live"); // live | rounds | bets | config | reports
  const [game, setGame] = useState("wingo");
  const [duration, setDuration] = useState(60);

  return (
    <div className="p-3 md:p-4 max-w-[1400px] mx-auto">
      <PageHeader title="Lottery Control Center" description="Manage Win Go, K3, 5D & TRX Win" />

      <div className="flex gap-2 mb-3 overflow-x-auto -mx-3 px-3 md:mx-0 md:px-0 md:flex-wrap scrollbar-thin">
        {[
          { k: "live", label: "Live Round" },
          { k: "rounds", label: "Rounds" },
          { k: "bets", label: "Bets" },
          { k: "config", label: "Config & Multipliers" },
          { k: "reports", label: "Reports (P/L)" },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`shrink-0 px-3 md:px-4 py-2 rounded-lg font-semibold text-sm transition ${
              tab === t.k ? "bg-emerald-500 text-white shadow" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
            data-testid={`admin-lottery-tab-${t.k}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {(tab === "live" || tab === "rounds" || tab === "bets") && (
        <div className="mb-4 space-y-2 md:space-y-0 md:flex md:flex-wrap md:items-center md:gap-3">
          <div className="flex items-center gap-2 overflow-x-auto -mx-3 px-3 md:mx-0 md:px-0 md:flex-wrap scrollbar-thin">
            <span className="text-slate-500 text-xs font-bold uppercase tracking-wide shrink-0">Game</span>
            {GAMES.map((g) => (
              <button key={g.key} onClick={() => setGame(g.key)}
                className={`shrink-0 px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1.5 transition ${
                  game === g.key ? "bg-slate-900 text-white shadow" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
                data-testid={`admin-game-${g.key}`}
              >
                <g.icon size={14} /> {g.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 overflow-x-auto -mx-3 px-3 md:mx-0 md:px-0 md:flex-wrap scrollbar-thin">
            <span className="text-slate-500 text-xs font-bold uppercase tracking-wide shrink-0 md:ml-2">Duration</span>
            {DURATIONS.map((d) => (
              <button key={d.s} onClick={() => setDuration(d.s)}
                className={`shrink-0 px-3 py-1.5 rounded-lg text-sm font-semibold transition ${
                  duration === d.s ? "bg-emerald-500 text-white shadow" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
                data-testid={`admin-duration-${d.s}`}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {tab === "live" && <LiveRoundTab game={game} duration={duration} />}
      {tab === "rounds" && <RoundsTab game={game} duration={duration} />}
      {tab === "bets" && <BetsTab game={game} />}
      {tab === "config" && <ConfigTab />}
      {tab === "reports" && <ReportsTab />}
    </div>
  );
}

// ─── LIVE ROUND TAB ─────────────────────────────
function LiveRoundTab({ game, duration }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [savingMode, setSavingMode] = useState(false);
  const [confirmPick, setConfirmPick] = useState(null); // { label, payload, projectedPnl, matchCount }

  // K3 dice state (a,b,c 1-6). 5D digits (A..E 0-9).
  const [k3Dice, setK3Dice] = useState([1, 1, 1]);
  const [d5Digits, setD5Digits] = useState([0, 0, 0, 0, 0]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/lottery/live", { params: { game, duration } });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    }
  }, [game, duration]);

  useEffect(() => {
    load();
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [load]);

  async function saveHouseControl(patch) {
    try {
      setSavingMode(true);
      const { data: res } = await api.post("/admin/lottery/house-control", {
        game, duration, ...patch,
      });
      setData((prev) => prev ? { ...prev, house_control: res.settings } : prev);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSavingMode(false);
    }
  }

  async function commitPick() {
    if (!confirmPick || !data?.round) return;
    const { payload, label } = confirmPick;
    try {
      setBusy(true);
      await api.post("/admin/lottery/set-result", { round_id: data.round.id, result: payload });
      toast.success(`Winner locked: ${label}`);
      await saveHouseControl({ mode: "manual" });
      setConfirmPick(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  }

  async function clearManualResult() {
    if (!data?.round) return;
    try {
      setBusy(true);
      await api.post("/admin/lottery/clear-manual-result", null, { params: { round_id: data.round.id } });
      toast.success("Locked winner cleared");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  }

  async function forceSettle() {
    if (!data?.round) return;
    if (!window.confirm("Settle this round NOW?")) return;
    try {
      setBusy(true);
      await api.post("/admin/lottery/force-settle", null, { params: { round_id: data.round.id } });
      toast.success("Round settled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  }

  // Look up projected PnL from outcomes list by key
  const outcomeIndex = useMemo(() => {
    const m = {};
    (data?.outcomes || []).forEach((o) => { m[o.key] = o; });
    return m;
  }, [data]);

  function askPick({ payload, label, key }) {
    const o = outcomeIndex[key] || {};
    setConfirmPick({
      payload, label,
      projectedPnl: o.if_wins_house_pnl ?? null,
      projectedPayout: o.if_wins_payout ?? null,
      matchCount: o.match_count ?? 0,
      matchReal: o.match_bet_real ?? 0,
      matchBot: o.match_bet_bot ?? 0,
    });
  }

  if (!data?.round) return <div className="text-slate-500">Loading live data…</div>;

  const r = data.round;
  const secs = data.time_left_seconds || 0;
  const mm = Math.floor(secs / 60);
  const ss = secs % 60;
  const locked = secs <= (data.lock_seconds || 10);
  const hc = data.house_control || {};
  const totals = data.totals || {};
  const outcomes = data.outcomes || [];
  const manualResult = r.manual_result;
  // Round progress (time_elapsed / duration)
  const progressPct = Math.min(100, Math.max(0, ((duration - secs) / Math.max(1, duration)) * 100));
  const isCritical = secs <= 15 && !locked;
  // Best pick auto-highlight: outcome with the highest projected House PnL
  const bestOutcomeKey = outcomes.length > 0 && outcomes[0].if_wins_house_pnl > 0 ? outcomes[0].key : null;

  return (
    <div className="space-y-3 md:space-y-4">
      {/* HERO ROW: timer + manual result banner */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700 text-white rounded-2xl p-4 md:p-5 shadow-lg relative overflow-hidden">
          {/* progress ring background */}
          <div className="absolute inset-x-0 bottom-0 h-1 bg-slate-700">
            <div className={`h-full transition-all duration-1000 ease-linear ${locked ? "bg-rose-500" : isCritical ? "bg-amber-400" : "bg-emerald-400"}`}
                 style={{ width: `${progressPct}%` }} />
          </div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] md:text-xs uppercase tracking-wider text-slate-300 font-bold">Current Round</div>
            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${locked ? "bg-rose-500" : isCritical ? "bg-amber-500" : "bg-emerald-500"}`}>
              {locked ? "🔒 LOCKED" : isCritical ? "⚠ CLOSING" : "OPEN"}
            </span>
          </div>
          <div className="font-mono text-sm md:text-lg font-bold truncate" data-testid="live-period">{r.period}</div>
          <div className="flex items-baseline gap-2 mt-2">
            <div className={`font-mono font-black text-4xl md:text-5xl tabular-nums ${locked ? "text-rose-300" : isCritical ? "text-amber-300 animate-pulse" : "text-emerald-300"}`} data-testid="live-time-left">
              {String(mm).padStart(2, "0")}:{String(ss).padStart(2, "0")}
            </div>
            <div className="text-[10px] md:text-xs text-slate-400 uppercase">time left</div>
          </div>
          <div className="mt-3 pt-3 border-t border-slate-600 grid grid-cols-3 gap-2 text-xs">
            <div><div className="text-slate-400 text-[10px] md:text-xs">Bets</div><div className="font-bold text-sm">{totals.bet_count || 0}</div></div>
            <div><div className="text-slate-400 text-[10px] md:text-xs">Real</div><div className="font-bold text-sm text-emerald-300 tabular-nums">{fmtMoney(totals.real_bet)}</div></div>
            <div><div className="text-slate-400 text-[10px] md:text-xs">Bot</div><div className="font-bold text-sm text-amber-300 tabular-nums">{fmtMoney(totals.bot_bet)}</div></div>
          </div>
        </div>

        <div className="md:col-span-2 rounded-2xl p-4 md:p-5 shadow-lg border-2 border-dashed transition-all"
             style={{ borderColor: manualResult ? "#10b981" : "#cbd5e1",
                      background: manualResult ? "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)" : "#f8fafc" }}>
          {manualResult ? (
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2 min-w-0">
                <Trophy className="text-emerald-600 shrink-0 mt-0.5" size={22} />
                <div className="min-w-0">
                  <div className="text-[10px] md:text-xs uppercase tracking-wider text-emerald-700 font-bold">🎯 Winner Locked</div>
                  <div className="text-xl md:text-2xl font-black text-emerald-800 mt-1 break-words" data-testid="locked-winner-display">
                    {fmtResultPretty(game, manualResult)}
                  </div>
                  {r.manual_set_by && (
                    <div className="text-[10px] md:text-[11px] text-emerald-600 mt-1 truncate">Set by {r.manual_set_by}{r.manual_set_at ? ` · ${new Date(r.manual_set_at).toLocaleTimeString()}` : ""}</div>
                  )}
                </div>
              </div>
              <button onClick={clearManualResult} disabled={busy || locked}
                      data-testid="btn-clear-manual"
                      className="shrink-0 px-2.5 md:px-3 py-1.5 rounded-lg bg-white border-2 border-emerald-300 hover:bg-emerald-50 text-emerald-700 text-xs md:text-sm font-bold flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed">
                <X size={14} /> Clear
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3 h-full min-h-[100px] md:min-h-[110px]">
              <Target className="text-slate-400 shrink-0" size={28} />
              <div className="min-w-0">
                <div className="text-base md:text-lg font-bold text-slate-700">No winner locked</div>
                <div className="text-xs md:text-sm text-slate-500">Pick an outcome below to force it as this round's winner. Best pick is highlighted automatically.</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* WINNER PICKER (large, prominent) */}
      <div className="bg-white rounded-2xl shadow-lg p-4 md:p-5">
        <div className="flex items-start md:items-center justify-between gap-2 mb-3 md:mb-4">
          <div className="min-w-0">
            <h3 className="font-black text-lg md:text-xl flex items-center gap-2"><Target size={20} className="text-rose-500 shrink-0" /> Set Winning Outcome</h3>
            <p className="text-[11px] md:text-xs text-slate-500 mt-0.5">Pick the outcome to force as winner. Best pick (max House P/L) is highlighted.</p>
          </div>
          {locked && (
            <div className="text-[10px] md:text-xs text-rose-500 font-bold flex items-center gap-1 bg-rose-50 px-2 py-1 rounded shrink-0">
              <Lock size={12} /> <span className="hidden md:inline">Bets closed — </span>Locked
            </div>
          )}
        </div>

        {(game === "wingo" || game === "trxwin") && (
          <WingoPicker outcomeIndex={outcomeIndex} onPick={askPick} lockedNumber={manualResult?.number} bestKey={bestOutcomeKey} />
        )}
        {game === "k3" && (
          <K3Picker outcomeIndex={outcomeIndex} onPick={askPick} lockedDice={manualResult?.dice} dice={k3Dice} setDice={setK3Dice} />
        )}
        {game === "5d" && (
          <FiveDPicker outcomeIndex={outcomeIndex} onPick={askPick} lockedDigits={manualResult?.digits} digits={d5Digits} setDigits={setD5Digits} />
        )}
      </div>

      {/* HOUSE CONTROL + DISTRIBUTION TABLE */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4">
        <div className="lg:col-span-1 bg-white rounded-2xl shadow p-4 md:p-5">
          <details className="lg:!open" open>
            <summary className="flex items-center justify-between cursor-pointer lg:cursor-default list-none">
              <h3 className="font-bold text-base md:text-lg">House Control</h3>
              <span className="text-xs text-slate-400 lg:hidden">tap to toggle</span>
            </summary>
            <div className="text-[11px] md:text-xs text-slate-500 mb-2 mt-3">Mode ({game.toUpperCase()} · {duration}s)</div>
            <div className="flex gap-1.5 md:gap-2 mb-4">
              <button onClick={() => saveHouseControl({ mode: "fair" })} disabled={savingMode} data-testid="hc-mode-fair"
                      className={`flex-1 py-2 px-2 rounded-lg font-bold text-xs md:text-sm transition ${hc.mode === "fair" ? "bg-sky-500 text-white shadow" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>🎲 FAIR</button>
              <button onClick={() => saveHouseControl({ mode: "auto" })} disabled={savingMode} data-testid="hc-mode-auto"
                      className={`flex-1 py-2 px-2 rounded-lg font-bold text-xs md:text-sm transition ${hc.mode === "auto" ? "bg-emerald-500 text-white shadow" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>🎯 AUTO</button>
              <button onClick={() => saveHouseControl({ mode: "manual" })} disabled={savingMode} data-testid="hc-mode-manual"
                      className={`flex-1 py-2 px-2 rounded-lg font-bold text-xs md:text-sm transition ${hc.mode === "manual" ? "bg-rose-500 text-white shadow" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>✋ MANUAL</button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-600">Target house edge</label>
                <div className="flex items-center gap-2">
                  <input type="number" min="0" max="0.5" step="0.01" inputMode="decimal"
                    defaultValue={Number(hc.target_edge || 0.15).toFixed(2)}
                    onBlur={(e) => saveHouseControl({ target_edge: Number(e.target.value) })}
                    className="w-24 px-2 py-1.5 border rounded text-sm" data-testid="hc-target-edge" />
                  <span className="text-xs text-slate-500">({((hc.target_edge || 0) * 100).toFixed(1)}%)</span>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Max payout cap (৳)</label>
                <input type="number" min="0" step="1000" inputMode="numeric"
                  defaultValue={hc.max_payout_cap || 50000}
                  onBlur={(e) => saveHouseControl({ max_payout_cap: Number(e.target.value) })}
                  className="w-full mt-1 px-2 py-1.5 border rounded text-sm" data-testid="hc-max-cap" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Min real-bet threshold (৳)</label>
                <input type="number" min="0" step="100" inputMode="numeric"
                  defaultValue={hc.min_real_bet_threshold || 500}
                  onBlur={(e) => saveHouseControl({ min_real_bet_threshold: Number(e.target.value) })}
                  className="w-full mt-1 px-2 py-1.5 border rounded text-sm" data-testid="hc-min-threshold" />
                <div className="text-[10px] text-slate-400 mt-1">Below this → auto mode falls back to fair</div>
              </div>
            </div>

            <button onClick={forceSettle} disabled={busy}
              className="w-full mt-4 py-2.5 rounded-lg bg-slate-900 text-white font-bold hover:bg-slate-800 flex items-center justify-center gap-2 text-sm active:scale-95 transition"
              data-testid="btn-force-settle">
              <Play size={14} /> Settle Now
            </button>
          </details>
        </div>

        <div className="lg:col-span-2 bg-white rounded-2xl shadow p-4 md:p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="min-w-0">
              <h3 className="font-bold text-base md:text-lg">Live Bet Distribution</h3>
              <div className="text-[11px] md:text-xs text-slate-500">Sorted by house profit if that outcome wins</div>
            </div>
            <button onClick={load} className="p-2 rounded-lg hover:bg-slate-100 shrink-0" title="Refresh">
              <RefreshCw size={16} />
            </button>
          </div>

          {outcomes.length === 0 ? (
            <div className="text-slate-400 text-center py-6 text-sm">No bets yet on this round</div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-slate-600 text-xs uppercase">
                      <th className="text-left px-2 py-2">Outcome</th>
                      <th className="text-right px-2 py-2">Match Bets</th>
                      <th className="text-right px-2 py-2">Real / Bot</th>
                      <th className="text-right px-2 py-2">If Wins Payout</th>
                      <th className="text-right px-2 py-2">House PnL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {outcomes.slice(0, 40).map((o, i) => {
                      const profitable = o.if_wins_house_pnl > 0;
                      return (
                        <tr key={o.key} className={`border-t hover:bg-emerald-50/40 ${i === 0 && profitable ? "bg-amber-50/60" : ""}`}>
                          <td className="px-2 py-2 font-semibold" data-testid={`outcome-${o.key}`}>
                            {i === 0 && profitable && <span className="mr-1 text-[9px] font-black uppercase text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">BEST</span>}
                            {o.label}
                          </td>
                          <td className="px-2 py-2 text-right tabular-nums">{o.match_count > 0 ? fmtMoney(o.match_bet_total) : <span className="text-slate-300">—</span>}</td>
                          <td className="px-2 py-2 text-right tabular-nums text-xs">
                            <span className="text-emerald-600">{fmtMoney(o.match_bet_real)}</span>
                            <span className="text-slate-300"> / </span>
                            <span className="text-amber-600">{fmtMoney(o.match_bet_bot)}</span>
                          </td>
                          <td className="px-2 py-2 text-right tabular-nums">{fmtMoney(o.if_wins_payout)}</td>
                          <td className={`px-2 py-2 text-right tabular-nums font-bold ${profitable ? "text-emerald-600" : "text-rose-500"}`}>
                            {profitable ? "+" : ""}{fmtMoney(o.if_wins_house_pnl)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* Mobile card list */}
              <div className="md:hidden space-y-2">
                {outcomes.slice(0, 20).map((o, i) => {
                  const profitable = o.if_wins_house_pnl > 0;
                  return (
                    <div key={o.key}
                         className={`rounded-lg border p-3 flex items-center justify-between gap-2 ${i === 0 && profitable ? "border-amber-300 bg-amber-50/60" : "border-slate-200 bg-white"}`}
                         data-testid={`outcome-mobile-${o.key}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          {i === 0 && profitable && <span className="text-[9px] font-black uppercase text-amber-700 bg-amber-200 px-1.5 py-0.5 rounded">BEST</span>}
                          <span className="font-bold text-sm truncate">{o.label}</span>
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          {o.match_count} bets · Payout {fmtMoney(o.if_wins_payout)}
                        </div>
                      </div>
                      <div className={`text-right shrink-0 font-black text-sm tabular-nums ${profitable ? "text-emerald-600" : "text-rose-500"}`}>
                        {profitable ? "+" : ""}{fmtMoney(o.if_wins_house_pnl)}
                      </div>
                    </div>
                  );
                })}
                {outcomes.length > 20 && (
                  <div className="text-xs text-slate-400 text-center pt-1">Showing top 20 of {outcomes.length}</div>
                )}
              </div>
              {outcomes.length > 40 && (
                <div className="hidden md:block text-xs text-slate-400 mt-2 text-center">Showing top 40 outcomes (sorted by house profit)</div>
              )}
            </>
          )}
        </div>
      </div>

      {/* CONFIRM MODAL */}
      {confirmPick && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/50 p-0 md:p-4" onClick={() => !busy && setConfirmPick(null)}>
          <div className="bg-white rounded-t-2xl md:rounded-2xl shadow-2xl w-full md:max-w-md p-5 md:p-6 animate-slide-up md:animate-none" onClick={(e) => e.stopPropagation()} data-testid="confirm-pick-modal">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center flex-shrink-0">
                <AlertTriangle size={20} />
              </div>
              <div className="flex-1">
                <div className="text-base md:text-lg font-black text-slate-900">Lock this outcome as winner?</div>
                <div className="text-xs md:text-sm text-slate-500 mt-0.5">This round will settle with the picked outcome.</div>
              </div>
            </div>
            <div className="bg-slate-50 rounded-xl p-3 md:p-4 space-y-2">
              <div className="flex justify-between text-sm gap-2"><span className="text-slate-500">Outcome</span><span className="font-bold text-slate-900 text-right">{confirmPick.label}</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-500">Matching bets</span><span className="font-bold">{confirmPick.matchCount}</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-500">Payout owed</span><span className="font-bold tabular-nums">{fmtMoney(confirmPick.projectedPayout)}</span></div>
              <div className="flex justify-between text-sm border-t pt-2"><span className="text-slate-500">House P/L</span>
                <span className={`font-black tabular-nums ${confirmPick.projectedPnl >= 0 ? "text-emerald-600" : "text-rose-500"}`}>
                  {confirmPick.projectedPnl >= 0 ? "+" : ""}{fmtMoney(confirmPick.projectedPnl)}
                </span>
              </div>
              <div className="text-[10px] md:text-[11px] text-slate-400">Real matched: {fmtMoney(confirmPick.matchReal)} · Bot matched: {fmtMoney(confirmPick.matchBot)}</div>
            </div>
            <div className="flex gap-2 mt-4 md:mt-5">
              <button onClick={() => setConfirmPick(null)} disabled={busy}
                      className="flex-1 py-3 rounded-lg border border-slate-300 hover:bg-slate-50 font-bold text-sm active:scale-95 transition"
                      data-testid="btn-cancel-pick">Cancel</button>
              <button onClick={commitPick} disabled={busy}
                      className="flex-1 py-3 rounded-lg bg-rose-500 text-white hover:bg-rose-600 font-bold text-sm disabled:opacity-60 active:scale-95 transition"
                      data-testid="btn-confirm-pick">
                {busy ? "Locking…" : "🎯 Lock Winner"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── WinGo / TRXWin picker ────────────────────────────────
function WingoPicker({ outcomeIndex, onPick, lockedNumber, bestKey }) {
  return (
    <div className="grid grid-cols-5 md:grid-cols-10 gap-1.5 md:gap-2">
      {[0,1,2,3,4,5,6,7,8,9].map((n) => {
        const key = `number:${n}`;
        const o = outcomeIndex[key] || {};
        const pnl = o.if_wins_house_pnl ?? 0;
        const profitable = pnl > 0;
        const isLocked = lockedNumber === n;
        const isBest = bestKey === key && !isLocked;
        const colors = wingoColor(n);
        const primary = colors[0];
        return (
          <button key={n}
            data-testid={`pick-number-${n}`}
            onClick={() => onPick({
              payload: { number: n },
              label: `Number ${n} (${colors.join(" + ")})`,
              key,
            })}
            className={`group relative rounded-xl p-2 md:p-3 flex flex-col items-center gap-1 md:gap-2 transition-all border-2 active:scale-95
              ${isLocked ? "border-emerald-500 bg-emerald-50 shadow-lg ring-2 ring-emerald-300"
               : isBest ? `border-amber-400 shadow-md ring-2 ring-amber-200 ${COLOR_CLASSES[primary].soft}`
                        : `border-transparent hover:border-slate-300 hover:shadow-md ${COLOR_CLASSES[primary].soft}`}
            `}>
            {isLocked && <div className="absolute -top-2 -right-2 bg-emerald-500 text-white text-[9px] md:text-[10px] font-black rounded-full px-1.5 md:px-2 py-0.5 shadow">LOCKED</div>}
            {isBest && <div className="absolute -top-2 -right-2 bg-amber-500 text-white text-[9px] md:text-[10px] font-black rounded-full px-1.5 md:px-2 py-0.5 shadow">BEST</div>}
            <WingoNumberBadge n={n} size="lg" />
            <div className="text-[9px] md:text-[10px] uppercase text-slate-500 tracking-wide capitalize font-semibold text-center leading-tight">{colors.join(" · ")}</div>
            <div className={`text-[10px] md:text-xs font-black tabular-nums ${profitable ? "text-emerald-600" : "text-rose-500"}`}>
              {profitable ? "+" : ""}{fmtMoney(pnl)}
            </div>
            <div className="text-[9px] md:text-[10px] text-slate-400">{o.match_count || 0} bets</div>
          </button>
        );
      })}
    </div>
  );
}

// ─── K3 dice picker ───────────────────────────────────────
function K3Picker({ outcomeIndex, onPick, lockedDice, dice, setDice }) {
  const key = `dice:${dice.join("")}`;
  const o = outcomeIndex[key] || {};
  const total = dice[0] + dice[1] + dice[2];
  const isLocked = lockedDice && lockedDice.length === 3 && lockedDice.every((d, i) => d === dice[i]);

  return (
    <div className="space-y-3 md:space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 md:gap-4">
        {[0, 1, 2].map((idx) => (
          <div key={idx} className="bg-slate-50 rounded-xl p-2.5 md:p-3">
            <div className="text-[11px] md:text-xs font-bold text-slate-600 mb-2 text-center">Dice {idx + 1}</div>
            <div className="grid grid-cols-6 sm:grid-cols-3 gap-1.5">
              {[1,2,3,4,5,6].map((v) => (
                <button key={v}
                  data-testid={`k3-dice-${idx}-${v}`}
                  onClick={() => { const nd = [...dice]; nd[idx] = v; setDice(nd); }}
                  className={`aspect-square rounded-lg font-black text-base md:text-xl transition active:scale-95 ${
                    dice[idx] === v ? "bg-slate-900 text-white shadow-lg scale-105"
                                    : "bg-white hover:bg-slate-100 text-slate-700 border border-slate-200"
                  }`}
                >{v}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between bg-slate-900 text-white rounded-xl p-3 md:p-4 gap-3">
        <div className="min-w-0">
          <div className="text-[10px] md:text-xs uppercase text-slate-400 tracking-wide">Selected</div>
          <div className="text-lg md:text-2xl font-black">🎲 {dice.join(" · ")} <span className="text-slate-300 text-sm md:text-lg">Σ{total}</span></div>
          <div className="text-[10px] md:text-xs text-slate-400 mt-0.5">{total >= 11 ? "Big" : "Small"} · {total % 2 === 1 ? "Odd" : "Even"}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] md:text-xs text-slate-400">House P/L</div>
          <div className={`text-lg md:text-2xl font-black tabular-nums ${(o.if_wins_house_pnl ?? 0) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
            {(o.if_wins_house_pnl ?? 0) >= 0 ? "+" : ""}{fmtMoney(o.if_wins_house_pnl ?? 0)}
          </div>
          <div className="text-[10px] md:text-[11px] text-slate-400">{o.match_count || 0} bets</div>
        </div>
      </div>
      <button
        data-testid="btn-pick-k3"
        onClick={() => onPick({
          payload: { dice },
          label: `Dice ${dice.join("-")} (Σ${total})`,
          key,
        })}
        disabled={isLocked}
        className={`w-full py-3 rounded-xl font-black text-white transition active:scale-95 ${
          isLocked ? "bg-emerald-500 cursor-not-allowed" : "bg-rose-500 hover:bg-rose-600 shadow-lg"
        }`}>
        {isLocked ? "✓ This is the locked winner" : `🎯 Lock ${dice.join("-")} as winner`}
      </button>
    </div>
  );
}

// ─── 5D digit picker ──────────────────────────────────────
function FiveDPicker({ outcomeIndex, onPick, lockedDigits, digits, setDigits }) {
  const total = digits.reduce((a, b) => a + b, 0);
  const isLocked = lockedDigits && lockedDigits.length === 5 && lockedDigits.every((d, i) => d === digits[i]);
  return (
    <div className="space-y-3 md:space-y-4">
      <div className="grid grid-cols-5 gap-1.5 md:gap-3">
        {["A","B","C","D","E"].map((label, idx) => (
          <div key={label} className="bg-slate-50 rounded-xl p-1.5 md:p-3">
            <div className="text-[10px] md:text-xs font-bold text-slate-600 mb-1.5 md:mb-2 text-center">Pos {label}</div>
            <div className="grid grid-cols-2 gap-1">
              {[0,1,2,3,4,5,6,7,8,9].map((v) => (
                <button key={v}
                  data-testid={`d5-${label}-${v}`}
                  onClick={() => { const nd = [...digits]; nd[idx] = v; setDigits(nd); }}
                  className={`py-1 md:py-1.5 rounded font-bold text-xs md:text-sm transition active:scale-90 ${
                    digits[idx] === v ? "bg-slate-900 text-white shadow" : "bg-white hover:bg-slate-100 text-slate-700 border border-slate-200"
                  }`}
                >{v}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between bg-slate-900 text-white rounded-xl p-3 md:p-4 gap-3">
        <div className="min-w-0">
          <div className="text-[10px] md:text-xs uppercase text-slate-400 tracking-wide">Selected</div>
          <div className="text-lg md:text-2xl font-black font-mono">{digits.join(" ")} <span className="text-slate-300 text-sm md:text-lg">Σ{total}</span></div>
          <div className="text-[10px] md:text-xs text-slate-400 mt-0.5">{total >= 23 ? "Big" : "Small"} · {total % 2 === 1 ? "Odd" : "Even"}</div>
        </div>
      </div>
      <button
        data-testid="btn-pick-5d"
        onClick={() => onPick({
          payload: { digits },
          label: `${digits.join("")} (Σ${total})`,
          key: `pos:${digits.join("")}`,
        })}
        disabled={isLocked}
        className={`w-full py-3 rounded-xl font-black text-white transition active:scale-95 ${
          isLocked ? "bg-emerald-500 cursor-not-allowed" : "bg-rose-500 hover:bg-rose-600 shadow-lg"
        }`}>
        {isLocked ? "✓ This is the locked winner" : `🎯 Lock ${digits.join("")} as winner`}
      </button>
    </div>
  );
}

// ─── ROUNDS TAB ─────────────────────────────
function RoundsTab({ game, duration }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const load = async () => {
    try {
      const { data } = await api.get("/admin/lottery/rounds", { params: { game, duration, status, limit: 100 } });
      setRows(data.items || []);
    } catch (e) { toast.error("Load failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [game, duration, status]);

  return (
    <div className="bg-white rounded-xl shadow p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold">Rounds</h3>
        <div className="flex gap-2">
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            className="px-2 py-1 border rounded" data-testid="round-status-filter">
            <option value="">All</option><option value="pending">Pending</option><option value="settled">Settled</option>
          </select>
          <button onClick={load} className="px-3 py-1 bg-slate-100 rounded hover:bg-slate-200 flex items-center gap-1">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>
      <div className="overflow-auto max-h-[70vh]">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 sticky top-0">
            <tr>
              <th className="text-left p-2">Period</th>
              <th className="text-left p-2">Status</th>
              <th className="text-left p-2">Result</th>
              <th className="text-right p-2">Bet</th>
              <th className="text-right p-2">Payout</th>
              <th className="text-right p-2">GGR</th>
              <th className="text-left p-2">Settled At</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const ggr = (r.total_bet || 0) - (r.total_payout || 0);
              return (
                <tr key={r.id} className="border-t hover:bg-slate-50" data-testid={`round-${r.period}`}>
                  <td className="p-2 font-mono">{r.period}</td>
                  <td className="p-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                      r.status === "settled" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                    }`}>{r.status}</span>
                  </td>
                  <td className="p-2 font-mono text-xs">{r.result ? JSON.stringify(r.result) : "—"}</td>
                  <td className="p-2 text-right">{fmtMoney(r.total_bet)}</td>
                  <td className="p-2 text-right">{fmtMoney(r.total_payout)}</td>
                  <td className={`p-2 text-right font-bold ${ggr >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtMoney(ggr)}</td>
                  <td className="p-2 text-xs text-slate-500">{r.settled_at ? new Date(r.settled_at).toLocaleString() : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── BETS TAB ─────────────────────────────
function BetsTab({ game }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const load = async () => {
    try {
      const { data } = await api.get("/admin/lottery/bets", { params: { game, status, limit: 200 } });
      setRows(data.items || []);
    } catch (e) { toast.error("Load failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [game, status]);

  return (
    <div className="bg-white rounded-xl shadow p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold">Bets</h3>
        <div className="flex gap-2">
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-2 py-1 border rounded">
            <option value="">All</option>
            <option value="open">Open</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
          </select>
          <button onClick={load} className="px-3 py-1 bg-slate-100 rounded hover:bg-slate-200"><RefreshCw size={14} /></button>
        </div>
      </div>
      <div className="overflow-auto max-h-[70vh]">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 sticky top-0">
            <tr>
              <th className="text-left p-2">User</th>
              <th className="text-left p-2">Game</th>
              <th className="text-left p-2">Period</th>
              <th className="text-left p-2">Bet</th>
              <th className="text-right p-2">Amount</th>
              <th className="text-left p-2">Status</th>
              <th className="text-right p-2">Payout</th>
              <th className="text-left p-2">Placed</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.id} className="border-t hover:bg-slate-50">
                <td className="p-2">{b.username}</td>
                <td className="p-2">{b.game} {b.duration}s</td>
                <td className="p-2 font-mono">{b.period}</td>
                <td className="p-2 font-mono text-xs">{b.bet_type}: {typeof b.bet_value === "object" ? JSON.stringify(b.bet_value) : b.bet_value}</td>
                <td className="p-2 text-right">{fmtMoney(b.amount)}</td>
                <td className="p-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                    b.status === "won" ? "bg-emerald-100 text-emerald-700" :
                    b.status === "lost" ? "bg-red-100 text-red-700" :
                    "bg-amber-100 text-amber-700"
                  }`}>{b.status}</span>
                </td>
                <td className="p-2 text-right">{fmtMoney(b.payout)}</td>
                <td className="p-2 text-xs text-slate-500">{new Date(b.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── CONFIG TAB ─────────────────────────────
function ConfigTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/lottery/config");
      setCfg(data);
    } catch (e) { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      await api.post("/admin/lottery/config", {
        multipliers: cfg.multipliers,
        enabled_games: cfg.enabled_games,
        house_edge: cfg.house_edge,
        min_bet: cfg.min_bet,
        max_bet: cfg.max_bet,
      });
      toast.success("Config saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!cfg) return <div>Loading...</div>;

  return (
    <div className="bg-white rounded-xl shadow p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-bold text-lg">Global Config</h3>
        <button onClick={save} disabled={saving}
          className="px-4 py-2 bg-emerald-500 text-white rounded-lg font-semibold flex items-center gap-2"
          data-testid="btn-save-config"
        >
          <Save size={16} /> Save
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div>
          <label className="text-sm font-semibold">Min Bet (BDT)</label>
          <input type="number" value={cfg.min_bet}
            onChange={(e) => setCfg({ ...cfg, min_bet: parseFloat(e.target.value) })}
            className="w-full mt-1 px-3 py-2 border rounded"
            data-testid="cfg-min-bet"
          />
        </div>
        <div>
          <label className="text-sm font-semibold">Max Bet (BDT)</label>
          <input type="number" value={cfg.max_bet}
            onChange={(e) => setCfg({ ...cfg, max_bet: parseFloat(e.target.value) })}
            className="w-full mt-1 px-3 py-2 border rounded"
            data-testid="cfg-max-bet"
          />
        </div>
        <div>
          <label className="text-sm font-semibold">House Edge (0-0.5)</label>
          <input type="number" step="0.01" value={cfg.house_edge}
            onChange={(e) => setCfg({ ...cfg, house_edge: parseFloat(e.target.value) })}
            className="w-full mt-1 px-3 py-2 border rounded"
            data-testid="cfg-house-edge"
          />
        </div>
        <div>
          <label className="text-sm font-semibold">Enabled Games</label>
          <div className="mt-1 flex flex-wrap gap-2">
            {GAMES.map((g) => (
              <label key={g.key} className="inline-flex items-center gap-1 text-sm bg-slate-100 px-2 py-1 rounded">
                <input type="checkbox"
                  checked={cfg.enabled_games?.includes(g.key)}
                  onChange={(e) => {
                    const next = new Set(cfg.enabled_games || []);
                    if (e.target.checked) next.add(g.key); else next.delete(g.key);
                    setCfg({ ...cfg, enabled_games: Array.from(next) });
                  }}
                  data-testid={`cfg-enabled-${g.key}`}
                />
                {g.label}
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t pt-4">
        <h4 className="font-bold mb-2">Payout Multipliers</h4>
        {GAMES.map((g) => {
          const m = cfg.multipliers?.[g.key] || {};
          return (
            <div key={g.key} className="mb-4">
              <div className="font-semibold text-sm mb-2 text-slate-700">{g.label}</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {Object.keys(m).map((k) => (
                  <div key={k} className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 font-mono w-24 truncate">{k}</span>
                    <input type="number" step="0.01" value={m[k]}
                      onChange={(e) => {
                        const nm = { ...(cfg.multipliers || {}) };
                        nm[g.key] = { ...(nm[g.key] || {}), [k]: parseFloat(e.target.value) };
                        setCfg({ ...cfg, multipliers: nm });
                      }}
                      className="flex-1 px-2 py-1 border rounded text-sm"
                      data-testid={`cfg-mult-${g.key}-${k}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── REPORTS TAB ─────────────────────────────
function ReportsTab() {
  const [rows, setRows] = useState([]);
  const [days, setDays] = useState(7);
  const load = async () => {
    try {
      const { data } = await api.get("/admin/lottery/reports", { params: { days } });
      setRows(data.stats || []);
    } catch (e) { toast.error("Load failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const totals = useMemo(() => rows.reduce((acc, r) => ({
    bet: acc.bet + (r.total_bet || 0),
    payout: acc.payout + (r.total_payout || 0),
    ggr: acc.ggr + (r.ggr || 0),
    count: acc.count + (r.bet_count || 0),
  }), { bet: 0, payout: 0, ggr: 0, count: 0 }), [rows]);

  return (
    <div className="bg-white rounded-xl shadow p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-bold text-lg">Reports (P/L)</h3>
        <select value={days} onChange={(e) => setDays(parseInt(e.target.value))}
          className="px-3 py-1 border rounded" data-testid="report-days">
          <option value={1}>Today</option>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      <div className="grid grid-cols-4 gap-3 mb-6">
        <div className="bg-blue-50 p-3 rounded-lg">
          <div className="text-xs text-slate-500">Total Bets</div>
          <div className="text-xl font-bold">{fmtMoney(totals.bet)}</div>
        </div>
        <div className="bg-amber-50 p-3 rounded-lg">
          <div className="text-xs text-slate-500">Total Payout</div>
          <div className="text-xl font-bold">{fmtMoney(totals.payout)}</div>
        </div>
        <div className={`p-3 rounded-lg ${totals.ggr >= 0 ? "bg-emerald-50" : "bg-red-50"}`}>
          <div className="text-xs text-slate-500">GGR (House P/L)</div>
          <div className={`text-xl font-bold ${totals.ggr >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtMoney(totals.ggr)}</div>
        </div>
        <div className="bg-slate-50 p-3 rounded-lg">
          <div className="text-xs text-slate-500">Bet Count</div>
          <div className="text-xl font-bold">{totals.count.toLocaleString()}</div>
        </div>
      </div>

      <table className="w-full text-sm">
        <thead className="bg-slate-50">
          <tr>
            <th className="text-left p-2">Game</th>
            <th className="text-right p-2">Total Bet</th>
            <th className="text-right p-2">Total Payout</th>
            <th className="text-right p-2">GGR</th>
            <th className="text-right p-2">Bets</th>
            <th className="text-right p-2">Won</th>
            <th className="text-right p-2">Lost</th>
            <th className="text-right p-2">Open</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r._id} className="border-t">
              <td className="p-2 font-semibold">{r._id}</td>
              <td className="p-2 text-right">{fmtMoney(r.total_bet)}</td>
              <td className="p-2 text-right">{fmtMoney(r.total_payout)}</td>
              <td className={`p-2 text-right font-bold ${r.ggr >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtMoney(r.ggr)}</td>
              <td className="p-2 text-right">{r.bet_count}</td>
              <td className="p-2 text-right text-emerald-600">{r.won_count}</td>
              <td className="p-2 text-right text-red-600">{r.lost_count}</td>
              <td className="p-2 text-right text-amber-600">{r.open_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
